// Corporate Catalogs list — forked from CatalogsPage but kept lean for Phase 1.
// Reuses the same /api/catalogs endpoint with ?type=corporate so the regular
// flow stays untouched. Each row links to the new corporate builder.
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { downloadPdfWithCache } from '../lib/pdf';
import { devError } from '../lib/logger';
import { formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Plus, Building2, Trash2, ExternalLink, Loader2, Copy, AlertCircle, Eye, FileText, Zap, CheckCircle2, Search, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import ShareCatalogMenu from '../components/ShareCatalogMenu';
import CatalogPreviewModal from '../components/CatalogPreviewModal';

const formatDate = (iso) => iso ? new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

export default function CorporateCatalogsPage() {
  const navigate = useNavigate();
  const [catalogs, setCatalogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [duplicateTarget, setDuplicateTarget] = useState(null);
  const [duplicating, setDuplicating] = useState(false);
  const [pdfBusy, setPdfBusy] = useState({});
  const [prewarmBusy, setPrewarmBusy] = useState({});
  const [previewCatalog, setPreviewCatalog] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Corporate search matches: title, subtitle, client_company, contact_person,
  // status, language, currency code. Case-insensitive.
  const filteredCatalogs = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return catalogs;
    return catalogs.filter(c => {
      const parts = [
        c.title, c.subtitle, c.status, c.language,
        c.currency?.code, c.currency?.symbol,
        c.corporate_meta?.client_company,
        c.corporate_meta?.contact_person,
      ].filter(Boolean).map(String).join(' ').toLowerCase();
      return parts.includes(q);
    });
  }, [catalogs, search]);

  const fetchCatalogs = async () => {
    setLoading(true); setError('');
    try {
      const { data } = await api.get('/catalogs?type=corporate');
      setCatalogs(data || []);
    } catch (err) { devError('Fetch corporate catalogs failed', err); setError(formatApiError(err.response?.data?.detail) || 'Failed to load corporate catalogs'); }
    setLoading(false);
  };

  useEffect(() => { fetchCatalogs(); }, []);

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete corporate catalog "${title}"? This cannot be undone.`)) return;
    try { await api.delete(`/catalogs/${id}`); toast.success('Catalog deleted'); fetchCatalogs(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || 'Delete failed'); }
  };

  const openDuplicate = (c) => setDuplicateTarget({
    id: c.id,
    original_title: c.title,
    title: `${c.title} (Copy)`,
    subtitle: c.subtitle || '',
    client_name: c.corporate_meta?.client_company || '',
  });

  const submitDuplicate = async () => {
    if (!duplicateTarget) return;
    const title = duplicateTarget.title.trim();
    if (!title) { toast.error('Title required'); return; }
    setDuplicating(true);
    try {
      const { data } = await api.post(`/corporate-catalogs/${duplicateTarget.id}/duplicate`, {
        title, subtitle: duplicateTarget.subtitle || null, client_name: duplicateTarget.client_name || null,
      });
      toast.success('Catalog duplicated');
      setDuplicateTarget(null);
      navigate(`/corporate-catalogs/${data.id}/edit`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || 'Duplicate failed'); }
    setDuplicating(false);
  };

  const downloadPdf = async (cid, title) => {
    setPdfBusy(s => ({ ...s, [cid]: true }));
    await downloadPdfWithCache(`/catalogs/${cid}/pdf`, title);
    setPdfBusy(s => ({ ...s, [cid]: false }));
  };

  const prewarmPdf = async (cid) => {
    setPrewarmBusy(s => ({ ...s, [cid]: true }));
    try {
      await api.post(`/catalogs/${cid}/prewarm`);
      toast.success('PDF generation started — refresh in ~30s');
      setTimeout(fetchCatalogs, 8000);
    } catch (err) { toast.error('Failed to start PDF generation'); devError('prewarm', err); }
    setPrewarmBusy(s => ({ ...s, [cid]: false }));
  };

  const openPreview = async (cid) => {
    try {
      const { data } = await api.get(`/catalogs/${cid}`);
      setPreviewCatalog(data);
      setPreviewOpen(true);
    } catch (err) { toast.error('Failed to open preview'); devError('preview', err); }
  };

  const totalCorp = catalogs.length;
  const shownCorp = filteredCatalogs.length;
  const isSearchingCorp = search.trim().length > 0;

  return (
    <div data-testid="corporate-catalogs-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <p className="text-[10px] tracking-[0.25em] uppercase text-[#C5A059] font-semibold mb-2">Corporate</p>
          <h2 className="font-serif text-3xl text-[#1A1A1A]">Corporate Catalogs</h2>
          <p className="text-sm text-[#5C5C5C] mt-1" data-testid="corp-catalogs-count">
            {loading
              ? 'Loading…'
              : isSearchingCorp
                ? `${shownCorp} of ${totalCorp} catalog${totalCorp === 1 ? '' : 's'} found`
                : 'Premium catalogs with MOQ pricing tiers for distributor & corporate clients.'}
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button onClick={fetchCatalogs} variant="outline" disabled={loading} className="rounded-sm border-[#E5E0D8]" data-testid="refresh-corporate-catalogs-btn">
            <Loader2 size={14} className={`mr-1 ${loading ? 'animate-spin' : 'hidden'}`} />Refresh
          </Button>
          <Button onClick={() => navigate('/corporate-catalogs/new')} className="bg-[#C5A059] hover:bg-[#B38F48] text-white rounded-sm" data-testid="create-corporate-catalog-btn">
            <Plus size={16} className="mr-2" />New Corporate Catalog
          </Button>
        </div>
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-sm flex items-center gap-2 text-sm text-[#9E2A2B]"><AlertCircle size={14} />{error}</div>}

      {/* Corporate search bar */}
      {!loading && totalCorp > 0 && (
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a8a8a] pointer-events-none" />
            <Input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by title, client, contact, status…"
              className="pl-9 pr-9 rounded-sm border-[#E5E0D8] bg-white"
              data-testid="corp-catalogs-search-input"
              aria-label="Search corporate catalogs"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#8a8a8a] hover:text-[#1A1A1A]"
                data-testid="corp-catalogs-search-clear"
                aria-label="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-16 text-sm text-[#5C5C5C]"><Loader2 className="animate-spin inline mr-2" size={16} />Loading…</div>
      ) : catalogs.length === 0 ? (
        <div className="bg-white border border-[#E5E0D8] rounded-sm p-16 text-center" data-testid="corporate-empty-state">
          <Building2 size={40} className="mx-auto text-[#C5A059] mb-3" />
          <p className="text-sm text-[#5C5C5C] mb-4">No corporate catalogs yet.</p>
          <Button onClick={() => navigate('/corporate-catalogs/new')} className="bg-[#C5A059] hover:bg-[#B38F48] text-white rounded-sm">Create your first Corporate Catalog</Button>
        </div>
      ) : filteredCatalogs.length === 0 ? (
        <div className="bg-white border border-[#E5E0D8] rounded-sm p-16 text-center" data-testid="corp-no-search-results">
          <Search size={36} className="mx-auto text-[#E5E0D8] mb-4" />
          <p className="text-[#1A1A1A] font-medium mb-2">No catalogs found</p>
          <p className="text-xs text-[#5C5C5C] mb-4">No corporate catalog matches &ldquo;{search}&rdquo;.</p>
          <Button onClick={() => setSearch('')} variant="outline" className="rounded-sm border-[#E5E0D8]" data-testid="corp-clear-search-empty-btn">Clear search</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCatalogs.map(c => {
            const client = c.corporate_meta?.client_company || '';
            return (
              <div key={c.id} className="bg-white border border-[#E5E0D8] rounded-sm p-5 hover:border-[#C5A059] transition-colors" data-testid={`corporate-catalog-card-${c.id}`}>
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-sm bg-[#F2E8D5] flex items-center justify-center shrink-0"><Building2 size={18} className="text-[#C5A059]" /></div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-medium text-[#1A1A1A] truncate" title={c.title}>{c.title}</h3>
                    {client && <p className="text-xs text-[#5C5C5C] truncate" title={client}>{client}</p>}
                    {!client && c.subtitle && <p className="text-xs text-[#5C5C5C] truncate">{c.subtitle}</p>}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-[10px] uppercase tracking-wider text-[#5C5C5C] mb-3 pb-3 border-b border-[#E5E0D8]">
                  <div><p className="text-[#1A1A1A] text-base font-medium">{c.section_count || 0}</p>Sections</div>
                  <div><p className="text-[#1A1A1A] text-base font-medium">{c.total_products || 0}</p>Products</div>
                  <div><p className={`text-base font-medium ${c.status === 'final' ? 'text-[#2D6A4F]' : 'text-[#C5A059]'}`}>{c.status || 'draft'}</p>Status</div>
                </div>
                <div className="flex items-center gap-2 mb-3 text-[10px] text-[#5C5C5C]">
                  <span>Updated {formatDate(c.updated_at)}</span>
                  {c.currency?.code && c.currency.code !== 'INR' && <span className="text-[#C5A059]">· {c.currency.code}</span>}
                  {c.pdf_ready ? (
                    <Badge className="bg-[#E8F5E9] text-[#2D6A4F] text-[10px] gap-1 ml-auto" data-testid={`corp-pdf-ready-${c.id}`}><CheckCircle2 size={10}/>PDF Ready</Badge>
                  ) : (
                    <Badge className="bg-[#F5F2EB] text-[#5C5C5C] text-[10px] ml-auto" data-testid={`corp-pdf-not-prepared-${c.id}`}>Not Prepared</Badge>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => openPreview(c.id)} className="rounded-sm border-[#E5E0D8] text-xs" data-testid={`preview-corporate-${c.id}`}><Eye size={12} className="mr-1"/>Preview</Button>
                  <Button size="sm" onClick={() => navigate(`/corporate-catalogs/${c.id}/edit`)} className="bg-[#C5A059] hover:bg-[#B38F48] text-white rounded-sm text-xs" data-testid={`edit-corporate-${c.id}`}>Edit</Button>
                  <Button size="sm" variant="outline" disabled={!!pdfBusy[c.id]} onClick={() => downloadPdf(c.id, c.title)} className="rounded-sm border-[#E5E0D8] text-xs" data-testid={`pdf-corporate-${c.id}`}>
                    {pdfBusy[c.id] ? <Loader2 size={12} className="mr-1 animate-spin"/> : <FileText size={12} className="mr-1"/>}PDF
                  </Button>
                  {!c.pdf_ready && (
                    <Button size="sm" variant="outline" disabled={!!prewarmBusy[c.id]} onClick={() => prewarmPdf(c.id)} className="rounded-sm border-[#C5A059] text-[#C5A059] hover:bg-[#F2E8D5] text-xs" data-testid={`prewarm-corporate-${c.id}`}>
                      {prewarmBusy[c.id] ? <Loader2 size={12} className="mr-1 animate-spin"/> : <Zap size={12} className="mr-1"/>}Prepare
                    </Button>
                  )}
                  <ShareCatalogMenu catalog={c} onDownloadPdf={() => downloadPdf(c.id, c.title)} pdfBusy={!!pdfBusy[c.id]} />
                  <Button size="sm" variant="outline" onClick={() => window.open(`/shared-mobile/${c.share_token}`, '_blank')} className="rounded-sm border-[#E5E0D8] text-xs" title="Open mobile view" data-testid={`open-corporate-${c.id}`}><ExternalLink size={12} className="mr-1"/>Open</Button>
                  <Button size="sm" variant="outline" onClick={() => openDuplicate(c)} className="rounded-sm border-[#E5E0D8] text-xs" data-testid={`duplicate-corporate-${c.id}`} title="Duplicate"><Copy size={12}/></Button>
                  <Button size="sm" variant="outline" onClick={() => handleDelete(c.id, c.title)} className="rounded-sm border-[#E5E0D8] text-xs text-[#9E2A2B] hover:bg-red-50" title="Delete" data-testid={`delete-corporate-${c.id}`}><Trash2 size={12}/></Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!duplicateTarget} onOpenChange={(o) => { if (!o) setDuplicateTarget(null); }}>
        <DialogContent className="bg-white rounded-sm border-[#E5E0D8]" data-testid="corp-duplicate-dialog">
          <DialogHeader><DialogTitle className="font-serif text-lg">Duplicate Corporate Catalog</DialogTitle></DialogHeader>
          {duplicateTarget && (
            <>
              <p className="text-xs text-[#5C5C5C] -mt-2">Cloning <span className="font-medium text-[#1A1A1A]">{duplicateTarget.original_title}</span>. All MOQ tiers, sections, products & client metadata copied.</p>
              <div className="space-y-4 py-4">
                <div><Label className="text-[10px] tracking-[0.15em] uppercase font-semibold mb-2 block">New Title *</Label>
                  <Input data-testid="corp-dup-title" autoFocus value={duplicateTarget.title} onChange={e => setDuplicateTarget(p => ({ ...p, title: e.target.value }))} className="rounded-sm border-[#E5E0D8]" />
                </div>
                <div><Label className="text-[10px] tracking-[0.15em] uppercase font-semibold mb-2 block">Subtitle</Label>
                  <Input data-testid="corp-dup-subtitle" value={duplicateTarget.subtitle} onChange={e => setDuplicateTarget(p => ({ ...p, subtitle: e.target.value }))} className="rounded-sm border-[#E5E0D8]" />
                </div>
                <div><Label className="text-[10px] tracking-[0.15em] uppercase font-semibold mb-2 block">Client / Company</Label>
                  <Input data-testid="corp-dup-client" value={duplicateTarget.client_name} onChange={e => setDuplicateTarget(p => ({ ...p, client_name: e.target.value }))} className="rounded-sm border-[#E5E0D8]" placeholder="Acme Corp" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" disabled={duplicating} onClick={() => setDuplicateTarget(null)} className="rounded-sm border-[#E5E0D8]">Cancel</Button>
                <Button onClick={submitDuplicate} disabled={duplicating} className="bg-[#C5A059] hover:bg-[#B38F48] text-white rounded-sm" data-testid="confirm-corp-duplicate-btn">
                  {duplicating ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Copy size={14} className="mr-2" />}{duplicating ? 'Duplicating…' : 'Create Copy'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <CatalogPreviewModal
        catalog={previewCatalog}
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        onDownloadPdf={previewCatalog ? () => downloadPdf(previewCatalog.id, previewCatalog.title) : null}
      />
    </div>
  );
}
